/*
 * Higher order Component for pages
 */

import { useEffect, useMemo } from 'react'
import { SWRConfig } from 'swr'
import { CookiesProvider } from 'react-cookie'
import { Provider } from 'react-redux'

import { AuthProvider } from 'utils/auth'
import { lsGet } from 'utils/request'
import store, { persistor } from 'store'
import { WebviewDetector } from 'utils/webview'
import PageLoadInitializer from 'utils/page-load'
import OmnichatProvider from 'components/common/OmnichatProvider'
import ThirdPartyScriptsLoader from 'components/common/ThirdPartyScriptsLoader'
import GlobalToastWrapper from 'components/common/GlobalToastWrapper'
import SyDealCustomerVerifyProvider from 'components/common/SyDealCustomerVerifyProvider'

const defaultOptions = {
  syDealCustomerVerify: true,
}

const swrConfig = { fetcher: lsGet }

const withLivingSinyi = (WrappedComponent, opts = {}) => {
  const LivingSinyiHOC = (props) => {
    useEffect(() => {
      if ('serviceWorker' in navigator) {
        window.addEventListener('load', () => {
          navigator.serviceWorker
            .register('/sw.js')
            .then(() => console.log('service worker registered'))
            .catch((err) => console.dir(err))
        })
      } else {
        console.log('service worker disabled')
      }
    }, [])

    const child = useMemo(() => {
      const options = { ...defaultOptions, ...opts }

      let child = <WrappedComponent {...props} />

      if (options.syDealCustomerVerify) {
        child = (
          <SyDealCustomerVerifyProvider>{child}</SyDealCustomerVerifyProvider>
        )
      }

      return child
    }, [props, WrappedComponent, opts])

    return (
      <Provider store={store}>
        <SWRConfig
          value={{
            ...swrConfig,
            fallback: props.swrFallback || {},
          }}
        >
          <CookiesProvider>
            <WebviewDetector>
              <AuthProvider defaultToken={props.token}>
                <PageLoadInitializer>
                  <ThirdPartyScriptsLoader />
                  <OmnichatProvider>
                    <GlobalToastWrapper>{child}</GlobalToastWrapper>
                  </OmnichatProvider>
                </PageLoadInitializer>
              </AuthProvider>
            </WebviewDetector>
          </CookiesProvider>
        </SWRConfig>
      </Provider>
    )
  }

  return LivingSinyiHOC
}

export default withLivingSinyi
